import { motion, useInView } from 'framer-motion'
import { Helmet } from 'react-helmet-async'
import { useNavigate } from 'react-router-dom'
import { useRef, useState, useEffect } from 'react'
import {
    Users, Code, Rocket, Heart, Globe, Award,
    ArrowRight, MapPin, Clock, DollarSign, 
    CheckCircle, Briefcase, GraduationCap, Coffee
} from 'lucide-react'
import LandingHeader from '../../components/landing/LandingHeader'
import LandingFooter from '../../components/landing/LandingFooter'

const Careers = () => {
    const navigate = useNavigate()
    const [isMobile, setIsMobile] = useState(false)

    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth <= 768)
        }
        checkMobile()
        window.addEventListener('resize', checkMobile)
        return () => window.removeEventListener('resize', checkMobile)
    }, [])

    const handleGetStarted = () => {
        navigate('/signin')
    }

    const handleApply = (jobTitle) => {
        // Open Gmail compose directly
        const gmailUrl = `https://mail.google.com/mail/?view=cm&fs=1&to=hr.talkidatechnologies@gmail.com&su=Application for ${encodeURIComponent(jobTitle)}`
        window.open(gmailUrl, '_blank')
    }

    const jobOpenings = [
        {
            id: 1,
            title: 'React.js Developer',
            department: 'Engineering',
            location: 'Remote',
            type: 'Full-time',
            experience: 'Fresher (No Experience Required)',
            salary: 'Based on Performance',
            schedule: '5 Days (Monday to Friday)',
            qualification: 'Any degree in Computer Science, IT, Software Engineering, or related field',
            description: 'Join our frontend team to build intuitive and responsive user interfaces for our AI-powered chatbot platform using React.js and modern web technologies. Perfect opportunity for freshers to start their career in tech.',
            requirements: [
                'Basic knowledge of React.js and JavaScript',
                'Understanding of HTML, CSS, and responsive design',
                'Willingness to learn modern frontend tools',
                'Strong problem-solving attitude',
                'Good communication skills and team collaboration'
            ]
        },
        {
            id: 2,
            title: 'QA / Tester',
            department: 'Quality Assurance',
            location: 'Remote',
            type: 'Full-time',
            experience: 'Fresher (No Experience Required)',
            salary: 'Based on Performance',
            schedule: '5 Days (Monday to Friday)',
            qualification: 'Any degree in Computer Science, IT, Software Engineering, or related field',
            description: 'Learn and ensure the quality of our AI chatbot platform through testing strategies and quality assurance processes. Great entry point into the tech industry.',
            requirements: [
                'Basic understanding of software testing concepts',
                'Attention to detail and analytical mindset',
                'Willingness to learn testing tools and frameworks',
                'Strong communication and documentation skills',
                'Eagerness to learn and grow in QA field'
            ]
        },
        {
            id: 3,
            title: 'Prompt Engineer',
            department: 'AI Research',
            location: 'Remote',
            type: 'Full-time',
            experience: 'Fresher (No Experience Required)',
            salary: 'Based on Performance',
            schedule: '5 Days (Monday to Friday)',
            qualification: 'Any degree in Computer Science, IT, Software Engineering, AI/ML, or related field',
            description: 'Learn to design and optimize prompts for AI models to improve conversational quality and user experience. Exciting opportunity to work with cutting-edge AI technology.',
            requirements: [
                'Interest in AI and Large Language Models',
                'Strong writing and communication skills',
                'Basic understanding of AI concepts (training provided)',
                'Creative thinking and problem-solving abilities',
                'Willingness to learn Python and AI frameworks'
            ]
        },
        {
            id: 4,
            title: 'Sales Marketer',
            department: 'Sales & Marketing',
            location: 'Remote',
            type: 'Full-time',
            experience: 'Fresher (No Experience Required)',
            salary: 'Based on Performance + Incentives',
            schedule: '5 Days (Monday to Friday)',
            qualification: 'Any degree in Business, Marketing, Commerce, or related field',
            description: 'Learn to drive growth by identifying potential clients and promoting our AI chatbot solutions to businesses. Great opportunity to build a career in tech sales.',
            requirements: [
                'Excellent communication and presentation skills',
                'Interest in technology and AI products',
                'Strong interpersonal and relationship-building abilities',
                'Self-motivated and target-oriented mindset',
                'Willingness to learn sales processes and CRM tools'
            ]
        },
        {
            id: 5,
            title: 'HR Specialist',
            department: 'Human Resources',
            location: 'Remote',
            type: 'Full-time',
            experience: 'Fresher (No Experience Required)',
            salary: 'Based on Performance',
            schedule: '5 Days (Monday to Friday)',
            qualification: 'BA, BBA, MBA, BCOM, MCOM, or any degree in Human Resources, Psychology, or related field',
            description: 'Support our growing team by learning recruitment, employee relations, and HR operations in a fast-paced tech environment. Perfect start for HR career.',
            requirements: [
                'Basic understanding of HR principles',
                'Strong interpersonal and communication skills',
                'Organized and detail-oriented approach',
                'Willingness to learn HR software and processes',
                'Ability to work in a remote-first environment'
            ]
        }
    ]

    const benefits = [
        {
            icon: Rocket,
            title: 'Growth Opportunities',
            description: 'Accelerate your career with learning opportunities and skill development programs'
        },
        {
            icon: Globe,
            title: 'Remote-First Culture',
            description: 'Work from anywhere with flexible hours and work-life balance'
        },
        {
            icon: Heart,
            title: 'Health & Wellness',
            description: 'Comprehensive health insurance and wellness programs for you and your family'
        },
        {
            icon: Coffee,
            title: 'Great Perks',
            description: 'Competitive salary, equity options, and amazing team events'
        },
        {
            icon: GraduationCap,
            title: 'Learning Budget',
            description: 'Annual learning budget for courses, conferences, and certifications'
        },
        {
            icon: Award,
            title: 'Recognition',
            description: 'Performance-based bonuses and recognition programs'
        }
    ]

    return (
        <>
            <Helmet>
                <title>Careers - Join Talkida Technologies | Build the Future of AI</title>
                <meta name="description" content="Join Talkida Technologies and help build the future of conversational AI. Explore exciting career opportunities in engineering, AI research, product, and more." />
                <meta name="keywords" content="careers, jobs, talkida, AI jobs, software engineer, machine learning, remote work, tech careers" />
            </Helmet>

            <div className="min-h-screen relative overflow-hidden bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
                <div className="relative z-10">
                    <LandingHeader onGetStarted={handleGetStarted} />

                    {/* Hero Section */}
                    <HeroSection isMobile={isMobile} />

                    {/* Why Join Us */}
                    <WhyJoinUsSection benefits={benefits} isMobile={isMobile} />

                    {/* Job Openings */}
                    <JobOpeningsSection jobOpenings={jobOpenings} onApply={handleApply} isMobile={isMobile} />

                    {/* CTA Section */}
                    <CTASection isMobile={isMobile} />

                    <LandingFooter />
                </div>
            </div>
        </>
    )
}

// Hero Section
const HeroSection = ({ isMobile }) => {
    const heroRef = useRef(null)
    const isInView = useInView(heroRef, { once: true, amount: 0.1 })

    return (
        <section ref={heroRef} className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8">
            <div className="max-w-7xl mx-auto text-center">
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={isInView || isMobile ? { opacity: 1, y: 0 } : {}}
                    transition={{ duration: isMobile ? 0 : 0.8 }}
                >
                    <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4">
                        Build the Future of <span className="bg-clip-text text-transparent bg-gradient-to-r from-purple-400 to-pink-400">AI with Us</span>
                    </h1>
                    <p className="text-xl text-white/70 max-w-2xl mx-auto mb-8">
                        Join our passionate team of innovators, engineers, and dreamers who are revolutionizing how businesses interact with their customers through AI.
                    </p>
                    <div className="flex flex-wrap justify-center gap-8 text-white/60">
                        <div className="flex items-center gap-2">
                            <Users className="w-5 h-5 text-purple-400" />
                            <span>Growing Team</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <Globe className="w-5 h-5 text-purple-400" />
                            <span>Remote-First</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <Rocket className="w-5 h-5 text-purple-400" />
                            <span>Innovation Focused</span>
                        </div>
                    </div>
                </motion.div>
            </div>
        </section>
    )
}

// Why Join Us Section
const WhyJoinUsSection = ({ benefits, isMobile }) => {
    const sectionRef = useRef(null)
    const isInView = useInView(sectionRef, { once: true, amount: 0.1 })

    return (
        <section ref={sectionRef} className="py-20 px-4 sm:px-6 lg:px-8 bg-white/5">
            <div className="max-w-7xl mx-auto">
                <motion.div
                    className="text-center mb-16"
                    initial={{ opacity: 0, y: 30 }}
                    animate={isInView ? { opacity: 1, y: 0 } : {}}
                >
                    <h2 className="text-4xl sm:text-5xl font-bold text-white mb-4">
                        Why <span className="bg-clip-text text-transparent bg-gradient-to-r from-purple-400 to-pink-400">Talkida?</span>
                    </h2>
                    <p className="text-xl text-white/70 max-w-2xl mx-auto">
                        We're not just building products, we're building the future. Join us and be part of something extraordinary.
                    </p>
                </motion.div>

                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {benefits.map((benefit, index) => (
                        <motion.div
                            key={index}
                            initial={{ opacity: 0, y: 50 }}
                            animate={isInView ? { opacity: 1, y: 0 } : {}}
                            transition={{ delay: index * 0.1, duration: 0.5 }}
                            className="bg-white/5 backdrop-blur-md p-8 rounded-2xl border border-white/10 hover:border-purple-500/50 transition-all duration-300 group"
                        >
                            <div className="w-14 h-14 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                                <benefit.icon className="w-7 h-7 text-white" />
                            </div>
                            <h3 className="text-xl font-semibold text-white mb-3">{benefit.title}</h3>
                            <p className="text-white/60 leading-relaxed">{benefit.description}</p>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    )
}

// Job Openings Section
const JobOpeningsSection = ({ jobOpenings, onApply, isMobile }) => {
    const sectionRef = useRef(null)
    const isInView = useInView(sectionRef, { once: true, amount: 0.1 })

    return (
        <section ref={sectionRef} className="py-20 px-4 sm:px-6 lg:px-8">
            <div className="max-w-7xl mx-auto">
                <motion.div
                    className="text-center mb-16"
                    initial={{ opacity: 0, y: 30 }}
                    animate={isInView ? { opacity: 1, y: 0 } : {}}
                >
                    <h2 className="text-4xl sm:text-5xl font-bold text-white mb-4">
                        Open <span className="bg-clip-text text-transparent bg-gradient-to-r from-purple-400 to-pink-400">Positions</span>
                    </h2>
                    <p className="text-xl text-white/70 max-w-2xl mx-auto">
                        Find your perfect role and help us build the next generation of AI-powered solutions.
                    </p>
                </motion.div>

                <div className="space-y-6">
                    {jobOpenings.map((job, index) => (
                        <motion.div
                            key={job.id}
                            initial={{ opacity: 0, y: 30 }}
                            animate={isInView ? { opacity: 1, y: 0 } : {}}
                            transition={{ delay: index * 0.1 }}
                            className="bg-white/5 backdrop-blur-md p-8 rounded-2xl border border-white/10 hover:border-purple-500/50 transition-all duration-300"
                        >
                            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
                                <div className="flex-1">
                                    <div className="flex flex-wrap items-center gap-4 mb-4">
                                        <h3 className="text-2xl font-bold text-white">{job.title}</h3>
                                        <span className="px-3 py-1 bg-purple-500/20 text-purple-300 rounded-full text-sm font-medium">
                                            {job.department}
                                        </span>
                                    </div>
                                    
                                    <div className="flex flex-wrap gap-6 mb-4 text-white/60">
                                        <div className="flex items-center gap-2">
                                            <MapPin className="w-4 h-4" />
                                            <span>{job.location}</span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <Clock className="w-4 h-4" />
                                            <span>{job.type}</span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <Briefcase className="w-4 h-4" />
                                            <span>{job.experience}</span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <GraduationCap className="w-4 h-4" />
                                            <span>{job.schedule}</span>
                                        </div>
                                    </div>
                                    
                                    <div className="mb-4">
                                        <h4 className="text-white font-semibold mb-2">Qualification Required:</h4>
                                        <p className="text-white/70 text-sm">{job.qualification}</p>
                                    </div>
                                    
                                    <p className="text-white/70 mb-4">{job.description}</p>
                                    
                                    <div className="space-y-2">
                                        <h4 className="text-white font-semibold">Key Requirements:</h4>
                                        <ul className="space-y-1">
                                            {job.requirements.map((req, idx) => (
                                                <li key={idx} className="flex items-start gap-2 text-white/60">
                                                    <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
                                                    <span className="text-sm">{req}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                </div>
                                
                                <div className="lg:ml-8">
                                    <button
                                        onClick={() => onApply(job.title)}
                                        className="w-full lg:w-auto px-8 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white font-semibold rounded-xl hover:from-purple-600 hover:to-pink-600 transition-all duration-300 flex items-center justify-center gap-2"
                                    >
                                        Apply Now
                                        <ArrowRight className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    )
}

// CTA Section
const CTASection = ({ isMobile }) => {
    const sectionRef = useRef(null)
    const isInView = useInView(sectionRef, { once: true, amount: 0.1 })

    return (
        <section ref={sectionRef} className="py-20 px-4 sm:px-6 lg:px-8 bg-white/5">
            <div className="max-w-4xl mx-auto text-center">
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={isInView ? { opacity: 1, y: 0 } : {}}
                    className="bg-gradient-to-br from-slate-800/40 via-purple-900/30 to-slate-800/40 backdrop-blur-xl border border-white/10 rounded-3xl p-12"
                >
                    <Users className="w-16 h-16 text-purple-400 mx-auto mb-6" />
                    <h2 className="text-4xl font-bold text-white mb-6">
                        Don't See Your Role?
                    </h2>
                    <p className="text-xl text-white/70 mb-8">
                        We're always looking for talented individuals to join our team. Send us your resume and let's talk about how you can contribute to our mission.
                    </p>
                    <motion.button
                        onClick={() => {
                            window.location.href = '/products'
                        }}
                        className="px-8 py-3 bg-white/10 hover:bg-white/20 backdrop-blur-sm border border-white/20 hover:border-white/30 text-white font-medium rounded-full transition-all duration-300 flex items-center gap-2 mx-auto"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                    >
                        View All Products & Pricing
                        <ArrowRight className="w-4 h-4" />
                    </motion.button>
                </motion.div>
            </div>
        </section>
    )
}

export default Careers